// Markers for a saved Shop Insurance policy whose quote no longer matches the
// client's details (`quoteOutdatedReason`, set by the shared calculation in
// `outdatedSavedQuote.ts`).
//
// Two forms share one label and one tooltip: a red "Quote invalid" badge for
// the policy's header, and red "Quote invalid" text that stands in for its ROI.
// The tooltip says what changed, then what to do. The reports print the same
// label and sentences. The tooltip is capped in width so it wraps to a few
// short lines. Both render nothing when the reason is empty, so callers can
// pass it through.

import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { cn } from '@design-system/lib/utils';
import { QUOTE_INVALID_MESSAGE, QUOTE_OUTDATED_EXPLANATION } from '@shared';
import { TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

function OutdatedQuoteTooltip({ reason, children }: { reason: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="max-w-72 text-left">
        <p>{reason}</p>
        <p className="mt-1 text-gray-300">{QUOTE_OUTDATED_EXPLANATION}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export function OutdatedQuoteBadge({
  reason,
  className,
}: {
  reason?: string | null;
  className?: string;
}) {
  if (!reason) return null;

  return (
    // The span is the trigger: the design-system Badge does not forward refs,
    // and without one the tooltip opens off-screen.
    <OutdatedQuoteTooltip reason={reason}>
      <span className={cn('inline-flex w-fit cursor-help', className)}>
        <Badge
          variant="destructive"
          className="border-destructive/40 bg-destructive/10 font-semibold"
        >
          <TriangleAlert />
          {QUOTE_INVALID_MESSAGE}
        </Badge>
      </span>
    </OutdatedQuoteTooltip>
  );
}

export function OutdatedQuoteText({
  reason,
  className,
}: {
  reason?: string | null;
  className?: string;
}) {
  if (!reason) return null;

  return (
    <OutdatedQuoteTooltip reason={reason}>
      <span
        className={cn(
          'text-destructive cursor-help underline decoration-dotted underline-offset-2',
          className,
        )}
      >
        {QUOTE_INVALID_MESSAGE}
      </span>
    </OutdatedQuoteTooltip>
  );
}
